import { useCallback, useMemo, useState } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import type { FieldErrors } from 'react-hook-form';
import { useZodForm, type UseZodFormReturn } from '@/components/form';
import {
  buildAttributesFormSchema,
  IdentifierKey,
  isEmailIdentified,
  toCustomerAttributes,
  type AttributeRow,
  type AttributesFormInput,
  type AttributesFormOutput,
  type CustomerAttributes,
  type PersonAttributesEnvironment,
} from '@/features/people';
import { getApiClientErrorStatus, getUserFacingErrorMessage } from '@/services/api/errors';
import { useCreateCustomer } from '@/services/db/customer/mutations';
import { getCustomer, getCustomersByEmail } from '@/services/db/customer/queries';
import type { Customer } from '@/services/db/customer/entity';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { generatePath } from '@/services/router/generate-path';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useNavigationGuard } from '@/services/navigation-guard';
import { useHistoryLeaveBlocker } from '@/services/navigation-guard/use-history-leave-blocker';
import { buildInitialAttributeRows, newPersonValidationRules } from './utils/initial-attributes';
import { classifyCreatePersonError, type CreatePersonError } from './utils/server-errors';

/**
 * The backend creates a profile asynchronously, so the page waits this long
 * before checking whether the record exists. Test runs skip the wait.
 */
export const CREATE_CHECK_DELAY_MS = import.meta.env.MODE === 'test' ? 0 : 3000;

/** One id for the pending and the "added soon" snackbars, so they replace each other. */
const CREATE_SNACKBAR_ID = 'person.create';
const SAVE_FAILED_MESSAGE = "Your changes couldn't be saved. Try again.";
/** The leave prompt's body while the form cannot be saved as it stands. */
const INVALID_LEAVE_MESSAGE = 'Discard your changes, or correct the errors to save.';

/** Thrown when the email lookup finds no profile yet; handled like a 404 from the by-id read. */
class CustomerNotYetCreatedError extends Error {
  constructor() {
    super('The profile is not queryable yet');
    this.name = 'CustomerNotYetCreatedError';
  }
}

/**
 * Whether the editor renders this row, and so whether an error placed on it
 * would be read. `cio_id` is never shown, and neither is `id` on a workspace
 * that identifies people by email.
 */
function isVisibleAttributeRow(
  row: AttributeRow | undefined,
  environment: Pick<PersonAttributesEnvironment, 'identifier_type'>,
): boolean {
  if (row == null || row.originalKey === IdentifierKey.CioId) {
    return false;
  }
  return !(isEmailIdentified(environment) && row.originalKey === IdentifierKey.Id);
}

/**
 * Thrown by the leave prompt's save when the person was not created, so the
 * guard cancels the navigation it was about to resume. The form has already
 * rendered the reason, so nothing here needs to report it again.
 */
export class PersonNotCreatedError extends Error {
  constructor() {
    super('The person was not created');
    this.name = 'PersonNotCreatedError';
  }
}

function isNotFound(error: unknown): boolean {
  return error instanceof CustomerNotYetCreatedError || getApiClientErrorStatus(error) === 404;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Whether any field carries an error the server reported, which the schema alone would not flag. */
export function hasServerError(errors: FieldErrors<AttributesFormInput>): boolean {
  const rows = errors.attributes;
  if (!Array.isArray(rows)) {
    return false;
  }
  return rows.some((row) => row?.value?.type === 'server' || row?.key?.type === 'server');
}

/**
 * The attribute form for a new person. Built once per environment: the
 * person's `created_at` is the moment the page opened.
 */
export function useNewPersonForm(environment: PersonAttributesEnvironment) {
  const defaultValues = useMemo<AttributesFormInput>(
    () => ({
      attributes: buildInitialAttributeRows(environment.identifiers, Math.floor(Date.now() / 1000)),
      hiddenAttrs: [],
    }),
    [environment.identifiers],
  );
  const schema = useMemo(() => buildAttributesFormSchema(newPersonValidationRules(environment)), [environment]);
  // Row errors surface as soon as a row is edited: the identifiers explain what
  // a person needs while the form is being filled in, not only on save.
  return useZodForm<AttributesFormInput>({ schema, defaultValues, mode: 'onChange' });
}

type UseCreatePersonFlowOptions = {
  workspaceId: number;
  environment: PersonAttributesEnvironment;
  form: UseZodFormReturn<AttributesFormInput>;
  /**
   * Read synchronously by the navigation guards; the flow clears it before it
   * navigates so its own exits are never blocked.
   */
  hasUnsavedChangesRef: React.MutableRefObject<boolean>;
};

type SaveOptions = {
  /**
   * Whether to leave the page once the create has settled. The leave prompt's
   * "Save and continue" passes `false`: the guard resumes the navigation the
   * user asked for.
   */
  shouldNavigate?: boolean;
};

/**
 * The add-person save: queue the create, map a rejection onto the form, then
 * poll once for the created profile and leave for its page — or for the
 * people list with an "added soon" notice when the backend has not caught up.
 */
export function useCreatePersonFlow({
  workspaceId,
  environment,
  form,
  hasUnsavedChangesRef,
}: UseCreatePersonFlowOptions) {
  const safeNavigate = useSafeNavigate();
  const snackbars = useSnackbars();
  const createCustomer = useCreateCustomer({ workspaceId });
  const taxonomy = useProductTaxonomy();

  const [isSaving, setIsSaving] = useState(false);
  const [suppressionErrorAttribute, setSuppressionErrorAttribute] = useState<string | null>(null);
  /** The attributes of the last save attempt; the header shows the person they describe. */
  const [submittedAttributes, setSubmittedAttributes] = useState<CustomerAttributes | null>(null);

  // The list ships at both taxonomy spellings; leave for the one this account's stage serves.
  const peopleHref = taxonomy.route('people.list').replace(':workspaceId', String(workspaceId));

  const leaveForm = useCallback(
    (href: string) => {
      hasUnsavedChangesRef.current = false;
      safeNavigate(href);
    },
    [hasUnsavedChangesRef, safeNavigate],
  );

  const applyServerError = useCallback(
    (serverError: CreatePersonError) => {
      switch (serverError.kind) {
        case 'suppressed':
          setSuppressionErrorAttribute(serverError.attribute);
          break;
        case 'attribute': {
          const rows = form.getValues('attributes');
          const index = rows.findIndex((row) => row.key === serverError.attribute);
          // A row the editor hides — `cio_id`, or `id` on an email-identified
          // workspace — would take the error where nobody can read it, so it
          // counts as no row at all and the message is reported instead.
          const isReadable = index !== -1 && isVisibleAttributeRow(rows[index], environment);
          if (isReadable) {
            form.setError(`attributes.${index}.value`, { type: 'server', message: serverError.detail });
          } else {
            snackbars.create({
              variant: 'critical',
              id: 'person.create.attribute',
              message: serverError.detail,
            });
          }
          break;
        }
        case 'coded':
          snackbars.create({ variant: 'critical', id: serverError.code, message: serverError.detail });
          break;
        default: {
          const _exhaustive: never = serverError;
          return _exhaustive;
        }
      }
      return undefined;
    },
    [environment, form, snackbars],
  );

  const findCreatedCustomer = useCallback(
    async (attributes: CustomerAttributes): Promise<Customer> => {
      const externalId = attributes.id ?? '';
      const email = attributes.email ?? '';
      // The id is the lookup key whenever the person was given one; an
      // email-identified workspace never identifies by id, so it always looks
      // the person up by email.
      if (externalId !== '' && !isEmailIdentified(environment)) {
        return getCustomer(workspaceId, externalId);
      }
      // A profile identified by phone alone has neither. An email query with
      // no email matches whichever profile the backend returns first, so there
      // is nothing to look the profile up by.
      if (email === '') {
        throw new CustomerNotYetCreatedError();
      }
      const [match] = await getCustomersByEmail(workspaceId, email);
      if (match == null) {
        throw new CustomerNotYetCreatedError();
      }
      return match;
    },
    [environment, workspaceId],
  );

  const confirmCreated = useCallback(
    async (attributes: CustomerAttributes, shouldNavigate: boolean) => {
      snackbars.create({
        id: CREATE_SNACKBAR_ID,
        message: `Creating the ${taxonomy.term('people.singular')}…`,
        autoCloseTimeout: 0,
      });
      await delay(CREATE_CHECK_DELAY_MS);

      try {
        const created = await findCreatedCustomer(attributes);
        snackbars.destroy(CREATE_SNACKBAR_ID);
        form.reset();
        if (shouldNavigate) {
          leaveForm(
            generatePath('/workspaces/:workspaceId/journeys/people/:personId', { workspaceId, personId: created.id }),
          );
        }
      } catch (error) {
        snackbars.destroy(CREATE_SNACKBAR_ID);
        if (!isNotFound(error)) {
          throw error;
        }
        snackbars.create({ id: CREATE_SNACKBAR_ID, message: 'Your customer will be added soon.' });
        form.reset();
        if (shouldNavigate) {
          leaveForm(peopleHref);
        }
      }
    },
    [findCreatedCustomer, form, leaveForm, peopleHref, snackbars, taxonomy, workspaceId],
  );

  const save = useCallback(
    async (values: AttributesFormOutput, { shouldNavigate = true }: SaveOptions = {}): Promise<boolean> => {
      setSuppressionErrorAttribute(null);
      const attributes = toCustomerAttributes(values);
      setSubmittedAttributes(attributes);
      setIsSaving(true);
      try {
        try {
          await createCustomer.mutateAsync({ attributes });
        } catch (error) {
          const serverError = classifyCreatePersonError(error);
          if (serverError == null) {
            throw error;
          }
          applyServerError(serverError);
          return false;
        }
        await confirmCreated(attributes, shouldNavigate);
        return true;
      } finally {
        setIsSaving(false);
      }
    },
    [applyServerError, confirmCreated, createCustomer],
  );

  /**
   * Validates, then saves, and reports whether the person was created. A
   * failure the form has no place for — an API error naming no attribute, a
   * create that never reached the server, a broken follow-up read — is
   * reported here with the server's detail when it has one.
   *
   * Callers that navigate on the result need the answer: failed validation and
   * a rejected create both leave the form on the page with its errors showing.
   */
  const submit = useCallback(
    async (options: SaveOptions = {}): Promise<boolean> => {
      let didCreate = false;
      try {
        await form.handleSubmit(async (values) => {
          didCreate = await save(values, options);
        })();
      } catch (error) {
        snackbars.create({
          variant: 'critical',
          id: 'person.create.failed',
          message: getUserFacingErrorMessage(error, SAVE_FAILED_MESSAGE),
        });
        return false;
      }
      return didCreate;
    },
    [form, save, snackbars],
  );

  const discard = useCallback(() => {
    setSuppressionErrorAttribute(null);
    form.reset();
    leaveForm(peopleHref);
  }, [form, leaveForm, peopleHref]);

  return { submit, discard, isSaving, suppressionErrorAttribute, submittedAttributes };
}

type UseUnsavedChangesPromptOptions = {
  hasUnsavedChangesRef: React.MutableRefObject<boolean>;
  form: UseZodFormReturn<AttributesFormInput>;
  isSaveDisabled: boolean;
  /** Saves without leaving; the prompt resumes the navigation the user asked for. */
  saveAndStay: () => Promise<void>;
};

/**
 * Prompts before unsaved edits are abandoned — on shell navigation, tab close,
 * and browser Back/Forward. "Save and continue" stays visible but disabled
 * while the form cannot be saved, with the prompt saying why.
 */
export function useUnsavedChangesPrompt({
  hasUnsavedChangesRef,
  form,
  isSaveDisabled,
  saveAndStay,
}: UseUnsavedChangesPromptOptions) {
  const discardEdits = useCallback(() => {
    hasUnsavedChangesRef.current = false;
    form.reset();
  }, [form, hasUnsavedChangesRef]);

  const leavePrompt = {
    hasUnsavedChanges: () => hasUnsavedChangesRef.current,
    message: isSaveDisabled ? INVALID_LEAVE_MESSAGE : undefined,
    onSave: saveAndStay,
    isSaveDisabled,
    onDiscard: discardEdits,
  };
  useNavigationGuard(leavePrompt);
  useHistoryLeaveBlocker(leavePrompt);
}
